/**
 * Shared task step types, step derivation logic, and step rendering components.
 * Used by both JobDetailPanel (legacy) and PhaseDetailView (new PipelinePage).
 *
 * Step label maps, derivation functions, and helpers extracted to
 * ./task-steps-helpers.tsx. Step header and details components extracted to
 * ./TaskStepHeader.tsx and ./TaskStepDetails.tsx. Status icon in ./TaskStepIcon.tsx.
 */
import React, { useState } from "react";

import { Box, Stack, Typography } from "@mui/material";

import type { TaskStep } from "./task-steps-helpers";
import { TaskStepDetails } from "./TaskStepDetails";
import { TaskStepHeader } from "./TaskStepHeader";

// ── Re-exports for backwards compatibility ────────────────────────────────────

export { pulse } from "./TaskStepIcon";
export { formatEventType } from "./task-steps-helpers";
export {
  type TaskStep,
  BUILD_PHASE_STEPS,
  CI_JOB_STEPS,
  DEPLOY_PHASE_STEPS,
  EVENT_STEP_MAP,
  deriveCIJobSteps,
  deriveEventSteps,
  deriveLiveLogSteps,
  deriveServiceBuildStep,
  formatStepDuration,
} from "./task-steps-helpers";

// `StepStatusIcon` extracted to ./TaskStepIcon.tsx.

// ── TaskStepsList Component ───────────────────────────────────────────────────

export function TaskStepsList({
  steps,
  defaultExpandedKey,
}: {
  steps: TaskStep[];
  defaultExpandedKey?: string;
}): React.ReactElement {
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(() => {
    if (defaultExpandedKey) return new Set([defaultExpandedKey]);
    const firstActive = steps.find(
      (s) => s.status === "failed" || s.status === "in_progress",
    );
    return firstActive ? new Set([firstActive.key]) : new Set();
  });

  const toggleStep = (key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (steps.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ pl: 2 }}>
        No tasks yet
      </Typography>
    );
  }

  return (
    <Stack spacing={0}>
      {steps.map((step) => {
        const isExpanded = expandedKeys.has(step.key);
        return (
          <Box key={step.key}>
            <TaskStepHeader
              step={step}
              isExpanded={isExpanded}
              onToggle={toggleStep}
            />
            <TaskStepDetails step={step} isExpanded={isExpanded} />
          </Box>
        );
      })}
    </Stack>
  );
}
